"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { IUserData, IUserSettingsCoin } from "@/data/api/auth-data";
import { ICoin } from "@/data/api/main-data";
import {
  fetchAllCoins,
  fetchLatestUserCoinByStatsHistory,
  fetchUserListAsMap,
} from "@/api/fetcher/shared-fetcher";
import { fetchUserCredentials } from "@/api/fetcher/page-main-logged-fetcher";
import { fetchUserSettings } from "@/api/fetcher/page-settings-fetcher";
import { ICoinSettingConfig } from '@/data/domain/data';
import { mapFromRecord, provideSession } from "@/app/utils";
import { AppConstants } from "@/app/constants";
import { getPrimaryCoinFromMap } from "@/app/shared/coin-utils";

export interface IAppContext {
  coins: Map<string, string[]> | undefined;
  coinsAsIs: Map<string, string[]> | undefined;
  coinsInfo: Map<string, ICoin> | undefined;
  coinsSettingsInfo: Map<string, ICoinSettingConfig> | undefined;
  isAdmin: boolean | undefined;
  canObserveUsers: boolean;
  userList: Map<string, IUserData> | undefined;
  selectedUser: string | undefined;
  credentials: IUserData | undefined;
  payoutsCoins: IUserSettingsCoin[] | undefined;
  hasMainInfo: boolean;
  fetchingMainInfo: boolean;
  linksTx: Map<string, string> | undefined;
  linksBlock: Map<string, string> | undefined;
  rememberedAlgorithm: string | undefined;
  rememberedCoin: string | undefined;
  updateCredentials: (data: IUserData) => void;
  fetchMainInfo: (
    session: string,
    success: () => void,
    error: (status: string) => void
  ) => void;
  rememberCoinSelection: (algorithm: string, coin: string) => void;
  selectUser: (user: string) => void;
  addUser: (user: IUserData) => void;
  refreshCoinsInfoNow: () => void;
  clearContext: () => void;
}

const AppContext = createContext<IAppContext | undefined>(undefined);
const COINS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const USER_SETTINGS_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const MAIN_INFO_RETRY_BASE_MS = 5_000;
const MAIN_INFO_RETRY_MAX_MS = 60_000;
const BACKGROUND_RETRY_BASE_MS = 5_000;
const BACKGROUND_RETRY_MAX_MS = 60_000;

function movePreferredUserToFront(
  users: Map<string, IUserData>,
  preferredLogin: string | undefined
): Map<string, IUserData> {
  if (!preferredLogin || !users.has(preferredLogin)) return users;

  const reordered = new Map<string, IUserData>();
  const preferredUser = users.get(preferredLogin);
  if (preferredUser) {
    reordered.set(preferredLogin, preferredUser);
  }

  for (const [login, data] of users) {
    if (login == preferredLogin) continue;
    reordered.set(login, data);
  }

  return reordered;
}

function resolveSelectedObservedLogin(
  users: Map<string, IUserData>,
  currentSelected: string | undefined,
  preferredLogin: string | undefined
): string | undefined {
  if (currentSelected != undefined && users.has(currentSelected)) {
    return currentSelected;
  }
  if (preferredLogin != undefined && users.has(preferredLogin)) {
    return preferredLogin;
  }
  return users.keys().next().value;
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [hasMainInfo, setHasMainInfo] = useState(false);
  const [fetchingMainInfo, setFetchingMainInfo] = useState(false);
  const [coins, setCoins] = useState<Map<string, string[]> | undefined>(
    new Map()
  );
  const [coinsAsIs, setCoinsAsIs] = useState<Map<string, string[]> | undefined>(
    new Map()
  );
  const [coinsInfo, setCoinsInfo] = useState<Map<string, ICoin> | undefined>(
    new Map()
  );
  const [coinsSettingsInfo, setCoinsSettingsInfo] = useState<Map<string, ICoinSettingConfig> | undefined>(
    new Map()
  );
  const [userList, setUserList] = useState<Map<string, IUserData> | undefined>(
    new Map()
  );
  const [selectedUser, setSelectedUser] = useState<string | undefined>(
    undefined
  );
  const [isAdmin, setIsAdminProp] = useState<boolean | undefined>(undefined);
  const [canObserveUsers, setCanObserveUsers] = useState(false);
  const [credentials, setCredentialsProp] = useState<IUserData | undefined>(
    undefined
  );
  const [payoutsCoins, setPayoutsCoinsProp] = useState<
    IUserSettingsCoin[] | undefined
  >(undefined);
  const [linksTx, setLinksTx] = useState<Map<string, string> | undefined>(
    new Map()
  );
  const [linksBlock, setLinksBlock] = useState<Map<string, string> | undefined>(
    new Map()
  );
  const [rememberedAlgorithm, setRememberedAlgorithm] = useState<
    string | undefined
  >(undefined);
  const [rememberedCoin, setRememberedCoin] = useState<string | undefined>(
    undefined
  );
  const coinsRefreshInProgressRef = useRef(false);
  const userSettingsRefreshInProgressRef = useRef(false);
  const coinsRefreshFailureStreakRef = useRef(0);
  const coinsRefreshRetryTimerRef = useRef<number | undefined>(undefined);
  const userSettingsRefreshFailureStreakRef = useRef(0);
  const userSettingsRefreshRetryTimerRef = useRef<number | undefined>(undefined);
  const refreshCoinsInfoRef = useRef<() => void>(() => {});
  const refreshUserSettingsRef = useRef<() => void>(() => {});
  const mainInfoFailureStreakRef = useRef(0);
  const mainInfoNextRetryAtRef = useRef(0);
  const mainInfoRetryTimerRef = useRef<number | undefined>(undefined);
  const mainInfoSuccessCallbackRef = useRef<(() => void) | undefined>(undefined);
  const mainInfoErrorCallbackRef = useRef<
    ((status: string) => void) | undefined
  >(undefined);

  const clearMainInfoRetryTimer = () => {
    if (mainInfoRetryTimerRef.current == undefined) return;
    window.clearTimeout(mainInfoRetryTimerRef.current);
    mainInfoRetryTimerRef.current = undefined;
  };

  const scheduleMainInfoRetryTick = () => {
    if (typeof window == "undefined") return;
    if (mainInfoRetryTimerRef.current != undefined) return;

    const delayMs = Math.max(mainInfoNextRetryAtRef.current - Date.now(), 0);
    mainInfoRetryTimerRef.current = window.setTimeout(() => {
      mainInfoRetryTimerRef.current = undefined;
      const retrySession = provideSession();
      const retrySuccess = mainInfoSuccessCallbackRef.current;
      const retryError = mainInfoErrorCallbackRef.current;
      if (!retrySession || !retrySuccess || !retryError) return;
      fetchMainInfo(retrySession, retrySuccess, retryError);
    }, delayMs);
  };

  const updateCredentials = (data: IUserData) => {
    setCredentialsProp(data);
  };

  const clearCoinsRefreshRetryTimer = () => {
    if (coinsRefreshRetryTimerRef.current == undefined) return;
    window.clearTimeout(coinsRefreshRetryTimerRef.current);
    coinsRefreshRetryTimerRef.current = undefined;
  };

  const scheduleCoinsRefreshRetry = useCallback(() => {
    if (typeof window == "undefined") return;
    if (coinsRefreshRetryTimerRef.current != undefined) return;
    const delay = provideRetryDelayMs(
      coinsRefreshFailureStreakRef.current,
      BACKGROUND_RETRY_BASE_MS,
      BACKGROUND_RETRY_MAX_MS
    );
    coinsRefreshRetryTimerRef.current = window.setTimeout(() => {
      coinsRefreshRetryTimerRef.current = undefined;
      refreshCoinsInfoRef.current();
    }, delay);
  }, []);

  const clearUserSettingsRefreshRetryTimer = () => {
    if (userSettingsRefreshRetryTimerRef.current == undefined) return;
    window.clearTimeout(userSettingsRefreshRetryTimerRef.current);
    userSettingsRefreshRetryTimerRef.current = undefined;
  };

  const scheduleUserSettingsRefreshRetry = useCallback(() => {
    if (typeof window == "undefined") return;
    if (userSettingsRefreshRetryTimerRef.current != undefined) return;
    const delay = provideRetryDelayMs(
      userSettingsRefreshFailureStreakRef.current,
      BACKGROUND_RETRY_BASE_MS,
      BACKGROUND_RETRY_MAX_MS
    );
    userSettingsRefreshRetryTimerRef.current = window.setTimeout(() => {
      userSettingsRefreshRetryTimerRef.current = undefined;
      refreshUserSettingsRef.current();
    }, delay);
  }, []);

  const fetchMainInfo = (
    session: string,
    success: () => void,
    error: (status: string) => void
  ) => {
    mainInfoSuccessCallbackRef.current = success;
    mainInfoErrorCallbackRef.current = error;

    if (fetchingMainInfo) return;
    if (Date.now() < mainInfoNextRetryAtRef.current) {
      scheduleMainInfoRetryTick();
      return;
    }
    clearMainInfoRetryTimer();

    setFetchingMainInfo(true);
    const finalizeSuccess = () => {
      setFetchingMainInfo(false);
      setHasMainInfo(true);
      mainInfoFailureStreakRef.current = 0;
      mainInfoNextRetryAtRef.current = 0;
      clearMainInfoRetryTimer();
      success();
    };

    const finalizeError = (status: string) => {
      setHasMainInfo(false);
      setFetchingMainInfo(false);
      mainInfoFailureStreakRef.current += 1;
      const retryDelay = provideMainInfoRetryDelayMs(
        mainInfoFailureStreakRef.current
      );
      mainInfoNextRetryAtRef.current = Date.now() + retryDelay;
      scheduleMainInfoRetryTick();
      error(status);
    };

    fetchAllCoins(
      (coinsMap, coinsAsIsMap, coinsInfoMap) => {
        setCoinsInfo(coinsInfoMap);
        setCoins(coinsMap);
        setCoinsAsIs(coinsAsIsMap);

        fetchUserCredentials(
          session,
          (userData) => {
            setCredentialsProp(userData);

            const isPrimaryAdmin =
              userData.login == AppConstants.PrimaryGuy;
            const isSecondaryObserver =
              userData.login == AppConstants.SecondaryGuy;
            const hasPrivilegedAccess = isPrimaryAdmin || isSecondaryObserver;
            setIsAdminProp(hasPrivilegedAccess);

            const applyStandardUserState = () => {
              setSelectedUser(undefined);
              setUserList(undefined);
              setCanObserveUsers(false);

              void fetchLatestUserCoinByStatsHistory(
                session,
                coinsMap,
                undefined,
                userData.registrationDate
              )
                .then((latestSelection) => {
                  if (!latestSelection) return;
                  setRememberedAlgorithm(latestSelection.algorithm);
                  setRememberedCoin(latestSelection.coin);
                })
                .finally(() => {
                  finalizeSuccess();
                });
            };

            const defaultCoin = getPrimaryCoinFromMap(coinsMap);
            if (!defaultCoin) {
              applyStandardUserState();
              return;
            }

            fetchUserListAsMap(
              session,
              defaultCoin,
              (usersFetched) => {
                if (usersFetched.size == 0) {
                  // Empty enumerate response means there is nothing to observe.
                  // Keep regular self-scope mode without target-user selectors.
                  applyStandardUserState();
                  return;
                }

                const normalizedUsers = movePreferredUserToFront(
                  usersFetched,
                  userData.login
                );
                const selectedLogin = resolveSelectedObservedLogin(
                  normalizedUsers,
                  selectedUser,
                  userData.login
                );

                setSelectedUser(selectedLogin);
                setUserList(normalizedUsers);
                setCanObserveUsers(true);
                finalizeSuccess();
              },
              (fetchUserListErrorStatus) => {
                if (hasPrivilegedAccess && !isSecondaryObserver) {
                  finalizeError(fetchUserListErrorStatus);
                  return;
                }

                // For regular users and observer fallback, lack of enumerate access
                // is not fatal: continue with own-account scope.
                applyStandardUserState();
              }
            );
          },
          (fetchUserCredentialsErrorStatus) => {
            finalizeError(fetchUserCredentialsErrorStatus);
          }
        );
      },
      (fetchAllCoinsErrorStatus) => {
        finalizeError(fetchAllCoinsErrorStatus);
      }
    );
  };

  const selectUser = (user: string) => {
    setSelectedUser(user);
  };

  const addUser = (user: IUserData) => {
    const updatedUserList = new Map(userList);
    updatedUserList.set(user.login!, user);
    setUserList(updatedUserList);
  };

  const rememberCoinSelection = (algorithm: string, coin: string) => {
    setRememberedAlgorithm(algorithm);
    setRememberedCoin(coin);
  };

  const clearContext = () => {
    setIsAdminProp(undefined);
    setCanObserveUsers(false);
    setCredentialsProp(undefined);
    setPayoutsCoinsProp(undefined);
    setSelectedUser(undefined);
    setUserList(undefined);
    setRememberedAlgorithm(undefined);
    setRememberedCoin(undefined);
    setHasMainInfo(false);
    mainInfoFailureStreakRef.current = 0;
    mainInfoNextRetryAtRef.current = 0;
    mainInfoSuccessCallbackRef.current = undefined;
    mainInfoErrorCallbackRef.current = undefined;
    coinsRefreshFailureStreakRef.current = 0;
    userSettingsRefreshFailureStreakRef.current = 0;
    clearMainInfoRetryTimer();
    clearCoinsRefreshRetryTimer();
    clearUserSettingsRefreshRetryTimer();
  };

  const refreshCoinsInfo = useCallback(() => {
    if (coinsRefreshInProgressRef.current) return;
    coinsRefreshInProgressRef.current = true;

    // Keep global coin metadata fresh for all pages (logged and not logged).
    void fetchAllCoins(
      (coinsMap, coinsAsIsMap, freshCoinsInfo) => {
        setCoins(coinsMap);
        setCoinsAsIs(coinsAsIsMap);
        setCoinsInfo(freshCoinsInfo);
        coinsRefreshFailureStreakRef.current = 0;
        clearCoinsRefreshRetryTimer();
      },
      (status) => {
        coinsRefreshFailureStreakRef.current += 1;
        scheduleCoinsRefreshRetry();
        console.warn("Failed to refresh coins info", status);
      }
    ).finally(() => {
      coinsRefreshInProgressRef.current = false;
    });
  }, [scheduleCoinsRefreshRetry]);
  refreshCoinsInfoRef.current = refreshCoinsInfo;

  const refreshCoinsInfoNow = useCallback(() => {
    refreshCoinsInfo();
  }, [refreshCoinsInfo]);

  const refreshUserSettings = useCallback(() => {
    if (userSettingsRefreshInProgressRef.current) return;
    if (!credentials?.login) {
      userSettingsRefreshFailureStreakRef.current = 0;
      clearUserSettingsRefreshRetryTimer();
      return;
    }

    const session = provideSession();
    if (!session) {
      userSettingsRefreshFailureStreakRef.current = 0;
      clearUserSettingsRefreshRetryTimer();
      return;
    }

    userSettingsRefreshInProgressRef.current = true;
    void fetchUserSettings(
      session,
      undefined,
      (userSettingsCoinsMap) => {
        setPayoutsCoinsProp(Array.from(userSettingsCoinsMap.values()));
        userSettingsRefreshFailureStreakRef.current = 0;
        clearUserSettingsRefreshRetryTimer();
      },
      (status) => {
        userSettingsRefreshFailureStreakRef.current += 1;
        scheduleUserSettingsRefreshRetry();
        console.warn("Failed to refresh user settings", status);
      }
    ).finally(() => {
      userSettingsRefreshInProgressRef.current = false;
    });
  }, [credentials?.login, scheduleUserSettingsRefreshRetry]);
  refreshUserSettingsRef.current = refreshUserSettings;

  useEffect(() => {
    refreshCoinsInfo();
    const timerId = window.setInterval(
      refreshCoinsInfo,
      COINS_REFRESH_INTERVAL_MS
    );

    return () => {
      window.clearInterval(timerId);
    };
  }, [refreshCoinsInfo]);

  useEffect(() => {
    if (!credentials?.login) return;

    refreshUserSettings();
    const timerId = window.setInterval(
      refreshUserSettings,
      USER_SETTINGS_REFRESH_INTERVAL_MS
    );

    return () => {
      window.clearInterval(timerId);
    };
  }, [credentials?.login, refreshUserSettings]);

  useEffect(() => {
    fetch("/row-links/links-tx.json")
      .then((res) => res.json())
      .then((data) => {
        const links = mapFromRecord<string>(data as Record<string, string>);
        setLinksTx(links);
      });

    fetch("/row-links/links-block.json")
      .then((res) => res.json())
      .then((data) => {
        const links = mapFromRecord<string>(data as Record<string, string>);
        setLinksBlock(links);
      });

      fetch("/coin-settings/coin-settings-map.json")
      .then((res) => res.json())
      .then((data) => {
        const links = mapFromRecord<ICoinSettingConfig>(
          data as Record<string, ICoinSettingConfig>
        );
        setCoinsSettingsInfo(links);
      });
  }, []);

  useEffect(() => {
    return () => {
      clearMainInfoRetryTimer();
      clearCoinsRefreshRetryTimer();
      clearUserSettingsRefreshRetryTimer();
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        coins,
        coinsAsIs,
        coinsInfo,
        isAdmin,
        canObserveUsers,
        userList,
        selectedUser,
        credentials,
        payoutsCoins,
        hasMainInfo,
        fetchingMainInfo,
        linksTx,
        linksBlock,
        rememberedAlgorithm,
        rememberedCoin,
        coinsSettingsInfo,
        updateCredentials,
        fetchMainInfo,
        rememberCoinSelection,
        selectUser,
        addUser,
        refreshCoinsInfoNow,
        clearContext,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

function provideMainInfoRetryDelayMs(failureStreak: number): number {
  const safeStreak = Math.max(1, Math.min(failureStreak, 8));
  const delay = MAIN_INFO_RETRY_BASE_MS * 2 ** (safeStreak - 1);
  return Math.min(delay, MAIN_INFO_RETRY_MAX_MS);
}

function provideRetryDelayMs(
  failureStreak: number,
  baseMs: number,
  maxMs: number
): number {
  const safeStreak = Math.max(1, Math.min(failureStreak, 8));
  const delay = baseMs * 2 ** (safeStreak - 1);
  return Math.min(delay, maxMs);
}

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
